import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Stack,
  Grid,
} from "@mui/material";
import {
  RocketLaunchOutlined as RocketIcon,
  AutoAwesome as SparkleIcon,
  Bolt as InstantIcon,
  ShieldOutlined as FairIcon,
  EmojiEventsOutlined as PrizeIcon,
  LocalFireDepartment as HotIcon,
  ConfirmationNumberOutlined as TicketIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";
import { Path, DEFAULT_CURRENCY, LOTTERY_NAMES } from "../../../../../static/Constants";
import { CommonChip, CommonBreadcrumbs } from "../../../../Components/ui";

const AllMissileHero = ({
  totalActive = 0,
  combinedPrize = "0.00",
  lowestEntry = "0.00",
  currency = DEFAULT_CURRENCY,
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      {/* Breadcrumbs Navigation */}
      <CommonBreadcrumbs
        items={[
          { label: "Home", path: Path.root },
          { label: "All Lotteries", path: Path.allLotteries },
          { label: `${LOTTERY_NAMES.ROCKETBET} Arena` },
        ]}
      />

      {/* Hero Banner Box */}
      <Box className="missile-hero-banner" sx={{ p: { xs: 3, md: 4 } }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={7}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ mb: 1.5 }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: borderRadiusPx.md,
                  bgcolor: colors.red[50],
                  color: colors.red[600],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(239, 68, 68, 0.15)",
                }}
              >
                <RocketIcon sx={{ fontSize: 26 }} />
              </Box>
              <div>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.red[600],
                    fontWeight: 700,
                    letterSpacing: 1.2,
                  }}
                >
                  {`${LOTTERY_NAMES.ROCKETBET.toUpperCase()} LIVE ARENA`}
                </Typography>
                <Typography variant="h4" className="missile-hero-title">
                  {`Autobet ${LOTTERY_NAMES.ROCKETBET}`}
                </Typography>
              </div>
            </Stack>

            <Typography
              variant="body1"
              sx={{
                color: colors.slate[600],
                maxWidth: 540,
                lineHeight: 1.6,
                mb: 2.5,
              }}
            >
              Experience decentralized rocket lottery action. Pick your 1 lucky trajectory coordinate, advance across 4 progressive tiers, and compete for transparent on-chain prize pools with verifiable VRF outcomes!
            </Typography>

            <Stack direction="row" gap={1.5} flexWrap="wrap">
              <CommonChip
                icon={<InstantIcon sx={{ fontSize: 16 }} />}
                label="Pick 1 Lucky Number"
                variant="neutral"
                size="small"
              />
              <CommonChip
                icon={<SparkleIcon sx={{ fontSize: 15 }} />}
                label="4 Progressive Tiers"
                variant="neutral"
                size="small"
              />
              <CommonChip
                icon={<FairIcon sx={{ fontSize: 15 }} />}
                label="Provably Fair VRF"
                variant="neutral"
                size="small"
              />
              <CommonChip
                icon={<PrizeIcon sx={{ fontSize: 15 }} />}
                label="Instant Smart Contract Payouts"
                variant="neutral"
                size="small"
              />
            </Stack>
          </Grid>

          {/* Quick Metrics Cards */}
          <Grid item xs={12} md={5}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box className="missile-stat-card">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <HotIcon sx={{ fontSize: 18, color: colors.red[600] }} />
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      Active Rockets
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: colors.slate[900] }}
                  >
                    {totalActive}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.emerald[600], fontWeight: 600 }}
                  >
                    ● Live on-chain
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box className="missile-stat-card">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <PrizeIcon sx={{ fontSize: 18, color: colors.amber[600] }} />
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      Total Prize Pool
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: colors.slate[900] }}
                    noWrap
                  >
                    {combinedPrize} {currency}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.slate[400], fontWeight: 500 }}
                  >
                    Across all draws
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box className="missile-stat-card">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <TicketIcon sx={{ fontSize: 18, color: colors.blue[600] }} />
                    <Typography
                      variant="caption"
                      sx={{ color: colors.slate[500], fontWeight: 600 }}
                    >
                      Entry From
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: colors.slate[900] }}
                  >
                    {lowestEntry} {currency}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.slate[400], fontWeight: 500 }}
                  >
                    Low ticket fees
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

AllMissileHero.propTypes = {
  totalActive: PropTypes.number,
  combinedPrize: PropTypes.string,
  lowestEntry: PropTypes.string,
  currency: PropTypes.string,
};

export default AllMissileHero;
